import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/Select';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { useCreateProject } from '@/features/projects/hooks';
import {
  DEPARTMENTS,
  departmentMeta,
  type DepartmentSlug,
} from '@/features/projects/departments';
import { formatError } from '@/lib/errors';
import { PeoplePicker } from './PeoplePicker';

// Sentinel for "no department" in <Select>, which can't represent an
// empty-string value.
const NO_DEPARTMENT = '__none__';

interface NewProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Department pre-selected when the dialog opens. The My tasks page
   * passes the active department filter so a project created while
   * looking at "Engineering" lands in Engineering; a department page
   * would pass its own slug.
   */
  defaultDepartment?: DepartmentSlug | null;
  /**
   * Called once the project row has been created (and the membership
   * writes succeeded). The dashboard page wires this to navigate the
   * user into the new project; tests can override.
   */
  onCreated?: (projectId: string) => void;
}

/**
 * "New project" modal — title, owning department, and share-with people
 * picker. The creator is auto-included by the DB trigger, so we render
 * them as a locked chip in the picker for visibility but never send
 * their id in the `memberProfileIds` array.
 *
 * Submitting navigates straight to the new project's detail page so
 * the user can start adding tasks immediately (the requested flow).
 */
export function NewProjectDialog({
  open,
  onOpenChange,
  defaultDepartment = null,
  onCreated,
}: NewProjectDialogProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { data: profiles, isLoading: profilesLoading } = useProfiles();
  const createProject = useCreateProject();

  const [name, setName] = useState('');
  const [department, setDepartment] = useState<string>(
    defaultDepartment ?? NO_DEPARTMENT,
  );
  const [memberIds, setMemberIds] = useState<string[]>([]);

  // Reset the form whenever the dialog re-opens so a previous draft
  // doesn't bleed through. We also clear it on close in case the user
  // hits Esc mid-typing. The department follows whatever filter the
  // opener is currently looking at.
  useEffect(() => {
    if (!open) {
      setName('');
      setMemberIds([]);
    }
    setDepartment(defaultDepartment ?? NO_DEPARTMENT);
  }, [open, defaultDepartment]);

  const nextDepartment = department === NO_DEPARTMENT ? null : department;

  // Profiles available for sharing: everyone in the org who is active,
  // minus the creator (who is auto-added). The creator is still
  // surfaced in the picker as a locked, pre-checked chip so the rule
  // is visible.
  const candidateProfiles = useMemo(
    () =>
      (profiles ?? [])
        .filter((p) => p.is_active)
        .map((p) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
        })),
    [profiles],
  );

  const lockedIds = profile ? [profile.id] : [];
  const pickerValue = useMemo(() => {
    // Show the creator as pre-selected so the share-with list always
    // includes them visibly, even though we strip them out of the
    // submission payload.
    if (!profile) return memberIds;
    if (memberIds.includes(profile.id)) return memberIds;
    return [profile.id, ...memberIds];
  }, [memberIds, profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) {
      toast.error('Not signed in.');
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Project name is required.');
      return;
    }
    try {
      const project = await createProject.mutateAsync({
        name: trimmed,
        department: nextDepartment,
        memberProfileIds: memberIds.filter((id) => id !== profile.id),
      });
      toast.success(`Created “${project.name}”`);
      onOpenChange(false);
      onCreated?.(project.id);
      navigate(`/projects/${project.id}`);
    } catch (err) {
      toast.error(formatError(err, 'Failed to create project.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="project-name">Title</Label>
              <Input
                id="project-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q3 Renew launch"
                maxLength={120}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-department">Department</Label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger
                  id="project-department"
                  className="h-11 w-full sm:h-10"
                >
                  {nextDepartment ? (
                    <span>{departmentMeta(nextDepartment)?.label}</span>
                  ) : (
                    <span className="text-muted-foreground">No department</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DEPARTMENT}>No department</SelectItem>
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d.slug} value={d.slug}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optional. Groups the list under the department that owns it;
                tasks still show up for everyone assigned.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Share with</Label>
              <PeoplePicker
                profiles={candidateProfiles}
                value={pickerValue}
                onChange={(next) => {
                  if (!profile) {
                    setMemberIds(next);
                    return;
                  }
                  setMemberIds(next.filter((id) => id !== profile.id));
                }}
                lockedIds={lockedIds}
                placeholder={
                  profilesLoading ? 'Loading people…' : 'Add Atlas users…'
                }
                disabled={profilesLoading}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={createProject.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createProject.isPending}>
              {createProject.isPending ? 'Creating…' : 'Create project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
